import React, { useEffect, useRef, useState } from "react";
import { useThemeStore } from "@shared/stores";

interface TooltipProps {
  message: string | React.ReactNode;
  children: React.ReactNode;
  position?: "top" | "bottom" | "left" | "right" | "bottom-start" | "bottom-end" | "top-end" | "top-start";
  className?: string;
  maxLines?: number;
  maxWidth?: number;
}

const Tooltip: React.FC<TooltipProps> = ({ message, children, position = "bottom", className, maxLines, maxWidth }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { isDarkMode } = useThemeStore();
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return { top: 0, left: 0 };

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const offset = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;

    switch (position) {
      case "top":
        top = triggerRect.top - tooltipRect.height - offset;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case "left":
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - offset;
        break;
      case "right":
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + offset;
        break;
      case "bottom-start":
        top = triggerRect.bottom + offset;
        left = triggerRect.left;
        break;
      case "bottom-end":
        top = triggerRect.bottom + offset;
        left = triggerRect.right - tooltipRect.width;
        break;
      case "top-end":
        top = triggerRect.top - tooltipRect.height - offset;
        left = triggerRect.right - tooltipRect.width + 10;
        break;
      case "top-start":
        top = triggerRect.top - tooltipRect.height - offset;
        left = triggerRect.left;
        break;
      default:
        top = triggerRect.bottom + offset;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    }

    left = Math.max(16, Math.min(left, viewportWidth - tooltipRect.width - 16));
    top = Math.max(0, Math.min(top, viewportHeight - tooltipRect.height));

    return { top, left };
  };

  const getTooltipTransform = () => {
    return "translateX(0) translateY(0)";
  };

  const getArrowPosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return {};

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const arrowSize = 12;

    const base = {
      position: "absolute" as const,
      zIndex: 51,
      width: arrowSize,
      height: arrowSize,
      backgroundColor: isDarkMode ? "#292929" : "white",
      transform: "rotate(45deg)",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    };

    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;
    const tooltipLeft = tooltipPosition.left;
    const tooltipTop = tooltipPosition.top;

    let arrowStyle = {};

    switch (position) {
      case "top":
        arrowStyle = {
          ...base,
          bottom: -6,
          left: Math.max(8, Math.min(triggerCenterX - tooltipLeft - arrowSize / 2, tooltipRect.width - 8)),
        };
        break;
      case "bottom":
        arrowStyle = {
          ...base,
          top: -6,
          left: Math.max(8, Math.min(triggerCenterX - tooltipLeft - arrowSize / 2, tooltipRect.width - 8)),
        };
        break;
      case "left":
        arrowStyle = {
          ...base,
          right: -6,
          top: Math.max(8, Math.min(triggerCenterY - tooltipTop - arrowSize / 2, tooltipRect.height - 8)),
        };
        break;
      case "right":
        arrowStyle = {
          ...base,
          left: -6,
          top: Math.max(8, Math.min(triggerCenterY - tooltipTop - arrowSize / 2, tooltipRect.height - 8)),
        };
        break;
      case "bottom-start":
        arrowStyle = {
          ...base,
          top: -6,
          left: Math.max(
            8,
            Math.min(triggerRect.left - tooltipLeft + triggerRect.width / 2 - arrowSize / 2, tooltipRect.width - 8)
          ),
        };
        break;
      case "bottom-end":
        arrowStyle = {
          ...base,
          top: -6,
          left: Math.max(
            8,
            Math.min(triggerRect.right - tooltipLeft - triggerRect.width / 2 - arrowSize / 2, tooltipRect.width - 8)
          ),
        };
        break;
      case "top-start":
        arrowStyle = {
          ...base,
          bottom: -6,
          left: Math.max(
            8,
            Math.min(triggerRect.left - tooltipLeft + triggerRect.width / 2 - arrowSize / 2, tooltipRect.width - 8)
          ),
        };
        break;
      case "top-end":
        arrowStyle = {
          ...base,
          bottom: -6,
          left: Math.max(
            8,
            Math.min(triggerRect.right - tooltipLeft - triggerRect.width / 2 - arrowSize / 2, tooltipRect.width - 8)
          ),
        };
        break;
      default:
        arrowStyle = {
          ...base,
          top: -6,
          left: Math.max(8, Math.min(triggerCenterX - tooltipLeft - arrowSize / 2, tooltipRect.width - 8)),
        };
    }

    return arrowStyle;
  };

  const getClassDivHidden = () => {
    const base = "absolute z-[55] w-full h-[8px] bg-white dark:bg-darkBackgroundColor rounded";
    switch (position) {
      case "bottom":
        return `${base} top-0`;
      case "top":
        return `${base} bottom-0`;
      case "left":
        return `${base} right-0`;
      case "right":
        return `${base} left-0`;
      case "bottom-start":
        return `${base} top-0`;
      case "bottom-end":
        return `${base} top-0`;
      case "top-end":
        return `${base} bottom-0`;
      case "top-start":
        return `${base} bottom-0`;
      default:
        return `${base} top-0`;
    }
  };

  const formatMessage = (message: string | React.ReactNode) => {
    if (typeof message === "string") {
      // Nếu có maxLines, không split thành <br /> để CSS line-clamp hoạt động đúng
      if (maxLines) {
        return message;
      }
      return message.split("\n").map((line, index) => (
        <React.Fragment key={index}>
          {line} <br />
        </React.Fragment>
      ));
    }
    return message;
  };

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      const position = calculatePosition();
      setTooltipPosition(position);
    }
  }, [isVisible, message]);

  useEffect(() => {
    if (isVisible) {
      const handleScroll = () => {
        setIsVisible(false);
      };

      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleScroll);

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleScroll);
      };
    }
    return undefined;
  }, [isVisible]);

  useEffect(() => {
    const handleResize = () => {
      const position = calculatePosition();
      setTooltipPosition(position);
    };
    handleResize();
  }, [message]);

  return (
    <>
      <div
        ref={triggerRef}
        className={`relative inline-block ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          className="fixed z-[99999] pointer-events-none"
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            transform: getTooltipTransform(),
            maxWidth: maxWidth || "auto",
          }}
        >
          <div
            className="relative max-w-[90vw] px-3 py-2 text-xs bg-white rounded shadow-elevationShadow4 dark:bg-darkBackgroundColor dark:text-white dark:shadow-elevationShadow4Dark break-words"
            style={
              maxLines
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: maxLines,
                    WebkitBoxOrient: "vertical" as const,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }
                : undefined
            }
          >
            {formatMessage(message)}
          </div>

          <div className={getClassDivHidden()}></div>

          <div style={getArrowPosition()} />
        </div>
      )}
    </>
  );
};

export default Tooltip;
